import { defineTour } from '../../types';
import { PERMISSIONS } from './access';

/**
 * T05 — Source a non-catalogue need (brief §7).
 *
 * For a seat that reads sourcing (`sourcing.rfq.view`, as the Sourcing entry
 * is). Starting and opening enquiries are explained only to `rfq.manage`, the
 * award only to `rfq.award` — the same gates as the screens. The queue is
 * `/sourcing` (GET only). The enquiry form (`/sourcing/new`) is a create form
 * and is never navigated to; its steps run only once the person has opened it
 * from a need. The enquiry steps run on an enquiry the person opened.
 *
 * An enquiry's page changes with its state: a draft shows the invitation
 * choice; an open one, who was invited and the comparison. A step whose part
 * of the page does not exist yet pauses with an access-safe explanation, and
 * the guide can be continued once the enquiry has moved on.
 *
 * Copy: `guidance.tours.T05.*` — draft, for native-language review.
 */
const FORM = {
  routeId: 'tenant.rfq-new',
  explainKey: 'guidance.tours.T05.openForm',
} as const;

const RECORD = {
  routeId: 'tenant.rfq',
  explainKey: 'guidance.tours.T05.openEnquiry',
} as const;

export const T05 = defineTour({
  id: 'T05',
  version: 1,
  portal: 'tenant',
  titleKey: 'guidance.tours.T05.title',
  summaryKey: 'guidance.tours.T05.summary',
  eligible: (ctx) => ctx.portal === 'tenant' && ctx.can(PERMISSIONS.rfqView),
  pageRoutes: ['tenant.sourcing', 'tenant.rfq-new', 'tenant.rfq'],
  steps: [
    {
      id: 't05-queue',
      anchor: 'tenant.sourcing.queue',
      route: 'tenant.sourcing',
      titleKey: 'guidance.tours.T05.queue.title',
      bodyKey: 'guidance.tours.T05.queue.body',
    },
    {
      id: 't05-lines',
      anchor: 'tenant.rfqnew.lines',
      needsRecord: FORM,
      when: (ctx) => ctx.can(PERMISSIONS.rfqManage),
      titleKey: 'guidance.tours.T05.lines.title',
      bodyKey: 'guidance.tours.T05.lines.body',
    },
    {
      id: 't05-draft',
      anchor: 'tenant.rfqnew.summary',
      needsRecord: FORM,
      when: (ctx) => ctx.can(PERMISSIONS.rfqManage),
      titleKey: 'guidance.tours.T05.draft.title',
      bodyKey: 'guidance.tours.T05.draft.body',
    },
    {
      // A draft: the invitation choice. Open or closed: who was invited.
      id: 't05-suppliers',
      anchor: ['tenant.rfq.invite', 'tenant.rfq.invited'],
      needsRecord: RECORD,
      titleKey: 'guidance.tours.T05.suppliers.title',
      bodyKey: 'guidance.tours.T05.suppliers.body',
    },
    {
      // A draft: "Ready to open?". Once open: the header with number and deadline.
      id: 't05-open',
      anchor: ['tenant.rfq.draft-summary', 'tenant.rfq.header'],
      needsRecord: RECORD,
      titleKey: 'guidance.tours.T05.open.title',
      bodyKey: 'guidance.tours.T05.open.body',
    },
    {
      id: 't05-comparison',
      anchor: 'tenant.rfq.comparison',
      needsRecord: RECORD,
      titleKey: 'guidance.tours.T05.comparison.title',
      bodyKey: 'guidance.tours.T05.comparison.body',
    },
    {
      id: 't05-decision',
      anchor: 'tenant.rfq.decision',
      needsRecord: RECORD,
      when: (ctx) => ctx.can(PERMISSIONS.rfqAward),
      titleKey: 'guidance.tours.T05.decision.title',
      bodyKey: 'guidance.tours.T05.decision.body',
      commitNote: true,
    },
  ],
});
